import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import type {
  InvoicesControllerListInvoicesParams,
  RecurringInvoiceResponseDto,
} from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { Checkbox } from '@trellis/roots/checkbox'
import {
  BulkActionBar,
  ColumnVisibilityMenu,
  DataTable,
  SummaryStat,
  SummaryStatGroup,
} from '@trellis/roots/data'
import { DateRangePicker } from '@trellis/roots/date-range-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { SearchInput } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { Avatar } from '@trellis/roots/media'
import { PageHeader, TabBar } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { StatusBadge } from '@trellis/roots/status'
import { BulkTagPicker, TagSelector } from '@trellis/roots/tags'
import { Typography } from '@trellis/roots/typography'
import { FEATURE } from '@trellis/shared'
import type { RecordPayment } from '@trellis/shared'
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreHorizontal,
  Plus,
  Repeat2,
  Tag,
  Trash2,
  TrendingUp,
  Upload,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { toast } from 'sonner'

import { FilterSheet } from '@/components/shell'
import {
  useArchiveInvoiceMutation,
  useAssignInvoiceTagsMutation,
  useBulkArchive,
  useBulkDelete,
  useBulkTagApply,
  useColumnVisibility,
  useDeleteInvoiceMutation,
  useDisclosure,
  useInvoicePayments,
  useInvoicesQuery,
  useInvoicesSummaryQuery,
  usePinnedColumn,
  useRecurringInvoicesQuery,
  useRecurringInvoiceStatsQuery,
  useRemoveInvoiceTagMutation,
  useTableSorting,
  useTransitionInvoiceStatusMutation,
  useTriggerRecurringInvoiceMutation,
  useUnarchiveInvoiceMutation,
  useUpdateRecurringInvoiceStatusMutation,
  useTagsQuery,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import {
  formatCalendarDate,
  formatCurrency,
  formatDate,
  getCurrencyFormatter,
  todayIsoDate,
} from '@/lib/format'
import { buildTagsColumn, filterGatedColumns } from '@/lib/table-columns'
import { cn } from '@/lib/utils'
import { exportInvoices } from '@/requests/data-export'
import { recordPayment } from '@/requests/invoices'
import type { InvoiceWithRecurring } from '@/types/invoice'

import {
  DuplicateInvoiceDialog,
  ReissueInvoiceDialog,
  InvoicePreviewSheet,
  InvoiceReminderChip,
  InvoiceRowActionsMenu,
  RINV_STATUS_LABELS,
  RINV_STATUS_STYLES,
  RecurringInvoiceRowActionsMenu,
} from './(components)'

type InvoiceStatus = InvoiceWithRecurring['status']

const invoicesRouteApi = getRouteApi('/_authenticated/_dashboard/invoices/')

const COLUMN_SPECS = [
  { id: 'invoiceNumber', label: 'Invoice #' },
  { id: 'clientName', label: 'Client' },
  { id: 'status', label: 'Status' },
  { id: 'total', label: 'Total' },
  { id: 'dueAt', label: 'Due' },
  { id: 'reminders', label: 'Reminders' },
  { id: 'createdAt', label: 'Created' },
  { id: 'tags', label: 'Tags' },
]

interface InvoiceFilters {
  status: InvoiceStatus | undefined
  dateRange: DateRange | undefined
}

export const ALL_INVOICE_STATUSES = [
  'DRAFT',
  'SENT',
  'PAID',
  'OVERDUE',
  'CANCELLED',
  'VOID',
] as const satisfies readonly InvoiceStatus[]

export const ALL_INVOICE_VIEWS = ['invoices', 'recurring'] as const

const ALL_STATUSES = ALL_INVOICE_STATUSES

const STATUS_TAB_LABELS: Record<InvoiceStatus | 'all', string> = {
  all: 'All',
  DRAFT: 'Draft',
  SENT: 'Sent',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
  VOID: 'Void',
}

const FILTERS_STORAGE_KEY = 'invoices-filters'

interface InvoicesQuerySearchParams {
  status?: InvoiceStatus
  archived: boolean
  createdFrom?: string
  createdTo?: string
}

/**
 * Turns the invoices route's validated search into the same options shape passed to
 * {@link useInvoicesQuery} here and to `invoicesInfiniteQueryOptions` in the route's
 * loader — keeping both on the same query key so the loader's prefetch is actually hit.
 * `tags` is intentionally excluded: it's local component state, not URL-synced.
 */
export function buildInvoicesQueryOptions(
  search: InvoicesQuerySearchParams,
): InvoicesControllerListInvoicesParams {
  const dateRangeComplete =
    search.createdFrom !== undefined && search.createdTo !== undefined

  return {
    status: search.status,
    limit: 20,
    archived: search.archived,
    ...(dateRangeComplete
      ? { createdFrom: search.createdFrom, createdTo: search.createdTo }
      : {}),
  }
}

function buildRecurringColumns(
  onTrigger: (id: string) => void,
  onPause: (id: string) => void,
  onActivate: (id: string) => void,
  onCancelClick: (recurringInvoice: RecurringInvoiceResponseDto) => void,
  isTriggerPending: boolean,
  isStatusPending: boolean,
): ColumnDef<RecurringInvoiceResponseDto>[] {
  return [
    {
      accessorKey: 'rinvLabel',
      header: 'Recurring invoice',
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.original.rinvLabel}</span>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      meta: { truncate: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.original.clientName} size="xs" />
          <span className="font-medium">{row.original.clientName}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          styles={RINV_STATUS_STYLES}
          labels={RINV_STATUS_LABELS}
        />
      ),
    },
    {
      accessorKey: 'subtotal',
      header: 'Subtotal',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.subtotal, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'frequency',
      header: 'Frequency',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm capitalize">
          {row.original.frequency.toLowerCase()}
        </span>
      ),
    },
    {
      accessorKey: 'nextRunAt',
      header: 'Next run',
      cell: ({ row }) =>
        row.original.nextRunAt ? (
          <span className="text-muted-foreground text-sm tabular-nums">
            {formatDate(row.original.nextRunAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <RecurringInvoiceRowActionsMenu
          recurringInvoice={row.original}
          isTriggerPending={isTriggerPending}
          isStatusPending={isStatusPending}
          onTrigger={onTrigger}
          onPause={onPause}
          onActivate={onActivate}
          onCancel={() => onCancelClick(row.original)}
        />
      ),
    },
  ]
}

function buildInvoiceColumns(
  onDeleteClick: (invoice: InvoiceWithRecurring) => void,
  onDuplicateClick: (invoice: InvoiceWithRecurring) => void,
  onReissueClick: (invoice: InvoiceWithRecurring) => void,
  onPreviewClick: (invoice: InvoiceWithRecurring) => void,
  onArchiveClick: (invoice: InvoiceWithRecurring) => void,
  onUnarchiveClick: (invoice: InvoiceWithRecurring) => void,
  onMarkSentClick: (invoice: InvoiceWithRecurring) => void,
  onMarkPaidClick: (invoice: InvoiceWithRecurring) => void,
  isTagsEnabled: boolean,
): ColumnDef<InvoiceWithRecurring>[] {
  return [
    {
      accessorKey: 'invoiceNumber',
      header: 'Invoice #',
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums">
          {row.original.invoiceNumber}
          {row.original.recurringInvoiceId && (
            <Link
              to="/invoices/recurring-invoices/$id"
              params={{ id: row.original.recurringInvoiceId }}
              onClick={(clickEvent) => clickEvent.stopPropagation()}
            >
              <span className="bg-muted text-muted-foreground inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium">
                ↻ RINV
              </span>
            </Link>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      meta: { truncate: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.original.clientName} size="xs" />
          <span className="font-medium">{row.original.clientName}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'total',
      header: 'Total',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.total, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'dueAt',
      header: 'Due',
      cell: ({ row }) =>
        row.original.dueAt ? (
          <span
            className={cn(
              'tabular-nums text-sm',
              row.original.status === 'OVERDUE'
                ? 'text-destructive-text font-medium'
                : 'text-muted-foreground',
            )}
          >
            {formatCalendarDate(row.original.dueAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'reminders',
      header: 'Reminders',
      enableSorting: false,
      cell: ({ row }) => <InvoiceReminderChip {...row.original} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm tabular-nums">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    ...(isTagsEnabled ? [buildTagsColumn<InvoiceWithRecurring>()] : []),
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <InvoiceRowActionsMenu
          invoice={row.original}
          onDeleteClick={onDeleteClick}
          onDuplicateClick={onDuplicateClick}
          onReissueClick={onReissueClick}
          onPreviewClick={onPreviewClick}
          onArchiveClick={onArchiveClick}
          onUnarchiveClick={onUnarchiveClick}
          onMarkSentClick={onMarkSentClick}
          onMarkPaidClick={onMarkPaidClick}
        />
      ),
    },
  ]
}

function readFiltersFromStorage(): Partial<InvoiceFilters> {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored) as {
      status?: InvoiceStatus
      dateFrom?: string
      dateTo?: string
    }
    return {
      status: parsed.status,
      dateRange:
        parsed.dateFrom || parsed.dateTo
          ? {
              from: parsed.dateFrom ? new Date(parsed.dateFrom) : undefined,
              to: parsed.dateTo ? new Date(parsed.dateTo) : undefined,
            }
          : undefined,
    }
  } catch {
    return {}
  }
}

function writeFiltersToStorage(filters: InvoiceFilters) {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        status: filters.status,
        dateFrom: filters.dateRange?.from?.toISOString(),
        dateTo: filters.dateRange?.to?.toISOString(),
      }),
    )
  } catch {
    // localStorage may be unavailable in some environments; silently ignore
  }
}

export function InvoicesPage() {
  useSetBreadcrumbs([{ label: 'Invoices' }])
  const {
    view,
    status: statusFilter,
    createdFrom: createdFromParam,
    createdTo: createdToParam,
    archived: showArchived,
  } = invoicesRouteApi.useSearch()
  const createdFrom = createdFromParam ? new Date(createdFromParam) : undefined
  const createdTo = createdToParam ? new Date(createdToParam) : undefined
  const deleteDialog = useDisclosure<InvoiceWithRecurring>()
  const cancelRecurringDialog = useDisclosure<RecurringInvoiceResponseDto>()
  const markSentDialog = useDisclosure<InvoiceWithRecurring>()
  const markPaidDialog = useDisclosure<InvoiceWithRecurring>()
  const invoicePreview = useDisclosure<string>()
  const duplicateDialog = useDisclosure<InvoiceWithRecurring>()
  const reissueDialog = useDisclosure<string>()
  const [searchQuery, setSearchQuery] = useState('')
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { columnVisibility, onColumnVisibilityChange } =
    useColumnVisibility('invoices-columns')
  const { sorting, onSortingChange } = useTableSorting('invoices-sorting')
  const { pinnedColumnId, onPinnedColumnChange } = usePinnedColumn(
    'invoices-pinned-column',
  )

  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const allTags = useTagsQuery()

  const navigate = useNavigate()
  const navigateSearch = invoicesRouteApi.useNavigate()

  const isRecurringView = view === 'recurring'

  const dateRange: DateRange | undefined =
    createdFrom || createdTo ? { from: createdFrom, to: createdTo } : undefined

  const dateRangeComplete = createdFrom !== undefined && createdTo !== undefined
  const hasActiveFilters = statusFilter !== undefined || dateRangeComplete

  // Restore last filter state from localStorage on first render if URL has no params
  const hasRestoredStoredFilters = useRef(false)
  useEffect(() => {
    if (hasRestoredStoredFilters.current) return
    hasRestoredStoredFilters.current = true
    if (
      statusFilter !== undefined ||
      createdFrom !== undefined ||
      createdTo !== undefined
    )
      return
    const stored = readFiltersFromStorage()
    if (!stored.status && !stored.dateRange?.from && !stored.dateRange?.to) return
    void navigateSearch({
      search: (prev) => ({
        ...prev,
        ...(stored.status ? { status: stored.status } : {}),
        ...(stored.dateRange?.from
          ? { createdFrom: stored.dateRange.from.toISOString() }
          : {}),
        ...(stored.dateRange?.to ? { createdTo: stored.dateRange.to.toISOString() } : {}),
      }),
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- run-once on mount; ref guard prevents re-runs

  const {
    isFetching,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  } = useInvoicesQuery({
    ...buildInvoicesQueryOptions({
      status: statusFilter,
      archived: showArchived,
      createdFrom: createdFromParam,
      createdTo: createdToParam,
    }),
    ...(tagFilter.length > 0 ? { tags: tagFilter } : {}),
  })

  const { data: summaryResult, isLoading: summaryLoading } = useInvoicesSummaryQuery()
  const allInvoices = summaryResult?.data ?? []

  // TODO: paginate if recurring invoice count grows beyond this cap
  const { currentPageData: recurringPageData } = useRecurringInvoicesQuery({ limit: 50 })
  const recurringInvoices = recurringPageData?.data ?? []

  const { data: statsResult } = useRecurringInvoiceStatsQuery(isRecurringView)

  const triggerMutation = useTriggerRecurringInvoiceMutation()
  const statusMutation = useUpdateRecurringInvoiceStatusMutation()

  const deleteMutation = useDeleteInvoiceMutation()
  const assignInvoiceTagsMutation = useAssignInvoiceTagsMutation()
  const removeInvoiceTagMutation = useRemoveInvoiceTagMutation()
  const archiveMutation = useArchiveInvoiceMutation()
  const unarchiveMutation = useUnarchiveInvoiceMutation()
  const transitionMutation = useTransitionInvoiceStatusMutation()
  const queryClient = useQueryClient()
  const recordPaymentMutation = useMutation({
    mutationFn: async ({
      invoiceId,
      data,
    }: {
      invoiceId: string
      data: RecordPayment
    }) => {
      const result = await recordPayment(invoiceId, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_result, { invoiceId }) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'payments', invoiceId] })
    },
  })
  const { data: markPaidPaymentsResult } = useInvoicePayments(
    markPaidDialog.value?.id ?? '',
  )
  const markPaidSucceededAmount = (markPaidPaymentsResult?.data ?? [])
    .filter((payment) => payment.status === 'SUCCEEDED')
    .reduce((sum, payment) => sum + Number(payment.amount), 0)
  const markPaidOutstandingAmount = markPaidDialog.value
    ? markPaidDialog.value.total - markPaidSucceededAmount
    : 0

  const rows = (currentPageData?.data ?? []) as InvoiceWithRecurring[]

  const trimmedQuery = searchQuery.trim().toLowerCase()
  const filteredRows = useMemo(
    () =>
      trimmedQuery
        ? rows.filter(
            (inv) =>
              inv.invoiceNumber.toLowerCase().includes(trimmedQuery) ||
              inv.clientName.toLowerCase().includes(trimmedQuery),
          )
        : rows,
    [rows, trimmedQuery],
  )

  const invoiceColumns = buildInvoiceColumns(
    deleteDialog.open,
    handleDuplicate,
    (invoice) => reissueDialog.open(invoice.id),
    (invoice) => invoicePreview.open(invoice.id),
    handleArchive,
    handleUnarchive,
    markSentDialog.open,
    markPaidDialog.open,
    isTagsEnabled,
  )
  const columnSpecs = filterGatedColumns(COLUMN_SPECS, isTagsEnabled)

  const outstandingTotal = allInvoices
    .filter((inv) => inv.status === 'SENT' || inv.status === 'OVERDUE')
    .reduce((sum, inv) => sum + inv.total, 0)
  const overdueTotal = allInvoices
    .filter((inv) => inv.status === 'OVERDUE')
    .reduce((sum, inv) => sum + inv.total, 0)
  const paidTotal = allInvoices
    .filter((inv) => inv.status === 'PAID')
    .reduce((sum, inv) => sum + inv.total, 0)

  const sentCount = allInvoices.filter((inv) => inv.status === 'SENT').length
  const overdueCount = allInvoices.filter((inv) => inv.status === 'OVERDUE').length
  const paidCount = allInvoices.filter((inv) => inv.status === 'PAID').length

  const currFmt = getCurrencyFormatter(undefined, { maximumFractionDigits: 0 })

  useEffect(() => {
    writeFiltersToStorage({ status: statusFilter, dateRange })
  }, [statusFilter, createdFrom, createdTo]) // eslint-disable-line react-hooks/exhaustive-deps -- dateRange is derived from createdFrom/createdTo; depending on primitives avoids an extra render without stale values

  useEffect(() => {
    setPageIndex(0)
  }, [statusFilter, createdFrom, createdTo, tagFilter, showArchived, setPageIndex])

  function handleStatusChange(status: InvoiceStatus | undefined) {
    void navigateSearch({ search: (prev) => ({ ...prev, status }) })
  }

  function handleDateRangeChange(range: DateRange | undefined) {
    void navigateSearch({
      search: (prev) => ({
        ...prev,
        createdFrom: range?.from?.toISOString(),
        createdTo: range?.to?.toISOString(),
      }),
    })
  }

  function handleClearFilters() {
    void navigateSearch({
      search: (prev) => ({
        ...prev,
        status: undefined,
        createdFrom: undefined,
        createdTo: undefined,
      }),
    })
    setSearchQuery('')
    setTagFilter([])
  }

  const selectedInvoices = filteredRows.filter((row) => rowSelection[row.id])

  function clearSelection() {
    setRowSelection({})
  }

  function deselectIds(ids: string[]) {
    setRowSelection((current) => {
      const next = { ...current }
      for (const id of ids) delete next[id]
      return next
    })
  }

  const {
    open: bulkDeleteOpen,
    setOpen: setBulkDeleteOpen,
    handleBulkDelete,
  } = useBulkDelete({
    selected: selectedInvoices,
    getId: (invoice) => invoice.id,
    deleteFn: (id) => deleteMutation.mutateAsync(id),
    label: 'invoice',
    onSuccess: clearSelection,
  })

  const {
    open: bulkTagPickerOpen,
    setOpen: setBulkTagPickerOpen,
    handleBulkTagApply,
    allSelectedTagIds,
    someSelectedTagIds,
  } = useBulkTagApply({
    selected: selectedInvoices,
    getId: (invoice) => invoice.id,
    getTags: (invoice) => invoice.tags ?? [],
    assignFn: (id, tagIds) =>
      assignInvoiceTagsMutation.mutateAsync({ invoiceId: id, tagIds }),
    removeFn: (id, tagId) =>
      removeInvoiceTagMutation.mutateAsync({ invoiceId: id, tagId }),
    invalidateKey: ['invoices'],
    onSuccess: clearSelection,
  })

  const { handleBulkArchive } = useBulkArchive({
    selected: selectedInvoices,
    getId: (invoice) => invoice.id,
    archiveFn: (id) => archiveMutation.mutateAsync(id),
    label: 'invoice',
    onSuccess: deselectIds,
  })

  function handleArchive(invoice: InvoiceWithRecurring) {
    archiveMutation.mutate(invoice.id, {
      onSuccess: () => toast.success('Invoice archived'),
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Failed to archive invoice'
        toast.error('Cannot archive invoice', { description: message })
      },
    })
  }

  function handleUnarchive(invoice: InvoiceWithRecurring) {
    unarchiveMutation.mutate(invoice.id, {
      onSuccess: () => toast.success('Invoice unarchived'),
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to unarchive invoice'
        toast.error('Cannot unarchive invoice', { description: message })
      },
    })
  }

  async function handleConfirmDelete() {
    if (!deleteDialog.value) return
    deleteDialog.close()
    try {
      await deleteMutation.mutateAsync(deleteDialog.value.id)
      toast.success('Invoice deleted')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Failed to delete invoice'
      toast.error('Cannot delete invoice', { description: message })
    }
  }

  async function handleConfirmMarkSent() {
    if (!markSentDialog.value) return
    const invoiceId = markSentDialog.value.id
    markSentDialog.close()
    try {
      await transitionMutation.mutateAsync({ id: invoiceId, status: 'SENT' })
      toast.success('Invoice marked as sent')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Failed to update status'
      toast.error('Failed to update status', { description: message })
    }
  }

  async function handleConfirmMarkPaid() {
    if (!markPaidDialog.value) return
    try {
      await recordPaymentMutation.mutateAsync({
        invoiceId: markPaidDialog.value.id,
        data: {
          amount: markPaidOutstandingAmount,
          paidAt: `${todayIsoDate()}T00:00:00.000Z`,
        },
      })
      toast.success('Invoice marked as paid')
      markPaidDialog.close()
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Failed to mark invoice as paid'
      toast.error('Failed to mark invoice as paid', { description: message })
    }
  }

  function handleDuplicate(invoice: InvoiceWithRecurring) {
    duplicateDialog.open(invoice)
  }

  async function handleTriggerRecurringInvoice(id: string) {
    try {
      await triggerMutation.mutateAsync(id)
      handleStatusChange(undefined)
      toast.success('Run triggered', {
        description: 'Invoice created from recurring schedule.',
      })
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Failed to trigger run'
      toast.error('Could not trigger run', { description: message })
    }
  }

  async function handlePauseRecurringInvoice(id: string) {
    try {
      await statusMutation.mutateAsync({ id, status: 'PAUSED' })
      toast.success('Recurring invoice paused')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Failed to pause recurring invoice'
      toast.error('Could not pause recurring invoice', { description: message })
    }
  }

  async function handleActivateRecurringInvoice(id: string) {
    try {
      await statusMutation.mutateAsync({ id, status: 'ACTIVE' })
      toast.success('Recurring invoice activated')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Failed to activate recurring invoice'
      toast.error('Could not activate recurring invoice', { description: message })
    }
  }

  async function handleConfirmCancelRecurringInvoice() {
    if (!cancelRecurringDialog.value) return
    cancelRecurringDialog.close()
    try {
      await statusMutation.mutateAsync({
        id: cancelRecurringDialog.value.id,
        status: 'CANCELLED',
      })
      toast.success('Recurring invoice cancelled')
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Failed to cancel recurring invoice'
      toast.error('Could not cancel recurring invoice', { description: message })
    }
  }

  const recurringColumns = useMemo(
    () =>
      buildRecurringColumns(
        handleTriggerRecurringInvoice,
        handlePauseRecurringInvoice,
        handleActivateRecurringInvoice,
        cancelRecurringDialog.open,
        triggerMutation.isPending,
        statusMutation.isPending,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerMutation.isPending, statusMutation.isPending],
  )

  const isFiltered = hasActiveFilters || !!trimmedQuery

  return (
    <div className="p-8">
      <PageHeader
        title="Invoices"
        subtitle="Invoice clients and track what's outstanding."
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="More options"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => exportInvoices()}>
                  <Download className="mr-2 size-4" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/invoices/import">
                    <Upload className="mr-2 size-4" />
                    Import CSV
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button asChild>
              <Link to="/invoices/new">
                <Plus className="size-4" />
                New invoice
              </Link>
            </Button>
          </>
        }
      />

      <SummaryStatGroup className="mb-5 sm:grid-cols-2 md:grid-cols-3">
        <SummaryStat
          label="Outstanding"
          icon={TrendingUp}
          value={currFmt.format(outstandingTotal)}
          sub={`${sentCount} sent · ${overdueCount} overdue`}
          isLoading={summaryLoading}
          valueClassName="text-xl md:text-3xl"
        />
        <SummaryStat
          label="Overdue"
          icon={AlertCircle}
          value={currFmt.format(overdueTotal)}
          sub="Past due date"
          tone={overdueTotal > 0 ? 'danger' : 'neutral'}
          isLoading={summaryLoading}
          valueClassName="text-xl md:text-3xl"
        />
        <SummaryStat
          label="Paid (12 mo)"
          icon={CheckCircle2}
          value={
            paidTotal >= 1000
              ? `$${(paidTotal / 1000).toFixed(1)}k`
              : currFmt.format(paidTotal)
          }
          sub={`${paidCount} invoices`}
          isLoading={summaryLoading}
          valueClassName="text-xl md:text-3xl"
        />
      </SummaryStatGroup>

      <div className="mb-6">
        <TabBar
          tabs={[
            { key: 'invoices', label: 'Invoices', count: allInvoices.length },
            { key: 'recurring', label: 'Recurring', count: recurringInvoices.length },
          ]}
          activeKey={view}
          onChange={(key) =>
            void navigateSearch({
              search: (prev) => ({
                ...prev,
                view: key as (typeof ALL_INVOICE_VIEWS)[number],
              }),
            })
          }
        />
      </div>

      {isRecurringView ? (
        <div>
          {statsResult?.data && (
            <div className="bg-muted/30 text-muted-foreground mb-4 rounded-lg border px-4 py-3 text-sm">
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.total}
              </span>{' '}
              Schedules ·{' '}
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.active}
              </span>{' '}
              Active ·{' '}
              <span className="text-foreground font-medium tabular-nums">
                {statsResult.data.paused}
              </span>{' '}
              Paused
            </div>
          )}

          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Repeat2 className="text-muted-foreground size-4" />
              <Typography variant="label" className="font-semibold">
                Recurring Invoices
              </Typography>
              <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium tabular-nums">
                {recurringInvoices.length}
              </span>
            </div>
          </div>
          <DataTable
            columns={recurringColumns}
            data={recurringInvoices}
            isLoading={false}
            emptyMessage="No recurring invoices."
            onRowClick={(recurringInvoice) =>
              void navigate({
                to: '/invoices/recurring-invoices/$id',
                params: { id: recurringInvoice.id },
              })
            }
          />
        </div>
      ) : (
        <>
          {selectedInvoices.length > 0 ? (
            <BulkActionBar
              selectedCount={selectedInvoices.length}
              actions={[
                ...(isTagsEnabled
                  ? [
                      {
                        label: 'Assign tags',
                        icon: <Tag className="size-4" />,
                        onClick: () => setBulkTagPickerOpen(true),
                      },
                    ]
                  : []),
                {
                  label: 'Archive',
                  icon: <Archive className="size-4" />,
                  onClick: () => void handleBulkArchive(),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 className="size-4" />,
                  onClick: () => setBulkDeleteOpen(true),
                  variant: 'destructive',
                },
              ]}
              onClearSelection={clearSelection}
              className="mb-2"
            />
          ) : (
            <div className="mb-2">
              <FilterSheet
                activeCount={
                  (statusFilter !== undefined ? 1 : 0) +
                  (dateRangeComplete ? 1 : 0) +
                  (tagFilter.length > 0 ? 1 : 0)
                }
                onClear={handleClearFilters}
                toolbar={
                  <>
                    <Select
                      value={statusFilter ?? 'all'}
                      onValueChange={(value) =>
                        handleStatusChange(
                          value === 'all' ? undefined : (value as InvoiceStatus),
                        )
                      }
                    >
                      <SelectTrigger
                        className={cn(
                          'h-9 w-[160px] rounded-lg font-normal',
                          !statusFilter && 'text-muted-foreground',
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {ALL_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {STATUS_TAB_LABELS[status]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
                    {isTagsEnabled && (
                      <TagSelector
                        tags={allTags.data ?? []}
                        value={tagFilter}
                        onChange={setTagFilter}
                        variant="filter"
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="invoices-show-archived"
                        checked={showArchived}
                        onCheckedChange={(checked) =>
                          void navigateSearch({
                            search: (prev) => ({ ...prev, archived: checked === true }),
                          })
                        }
                      />
                      <Label
                        htmlFor="invoices-show-archived"
                        className="text-muted-foreground text-sm font-normal"
                      >
                        Show archived
                      </Label>
                    </div>
                    <div className="flex-1" />
                    <SearchInput
                      placeholder="Search invoice # or client…"
                      value={searchQuery}
                      onChange={(changeEvent) => setSearchQuery(changeEvent.target.value)}
                      className="max-w-sm"
                    />
                    <ColumnVisibilityMenu
                      columns={columnSpecs}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={onColumnVisibilityChange}
                    />
                  </>
                }
                sheet={
                  <>
                    <Select
                      value={statusFilter ?? 'all'}
                      onValueChange={(value) =>
                        handleStatusChange(
                          value === 'all' ? undefined : (value as InvoiceStatus),
                        )
                      }
                    >
                      <SelectTrigger
                        className={cn(
                          'h-9 w-full rounded-lg font-normal',
                          !statusFilter && 'text-muted-foreground',
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {ALL_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {STATUS_TAB_LABELS[status]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
                    {isTagsEnabled && (
                      <TagSelector
                        tags={allTags.data ?? []}
                        value={tagFilter}
                        onChange={setTagFilter}
                        variant="filter"
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="invoices-show-archived-sheet"
                        checked={showArchived}
                        onCheckedChange={(checked) =>
                          void navigateSearch({
                            search: (prev) => ({ ...prev, archived: checked === true }),
                          })
                        }
                      />
                      <Label
                        htmlFor="invoices-show-archived-sheet"
                        className="text-muted-foreground text-sm font-normal"
                      >
                        Show archived
                      </Label>
                    </div>
                  </>
                }
              />
            </div>
          )}

          <div className="mb-3 flex min-h-5 items-center justify-between">
            <Typography variant="body-sm" color="muted">
              Showing{' '}
              <span className="text-foreground font-medium tabular-nums">
                {filteredRows.length}
              </span>{' '}
              invoices on this page
              {isFiltered ? ' · filtered' : ''}
            </Typography>
            {isFiltered && (
              <button
                onClick={handleClearFilters}
                className="text-muted-foreground hover:text-foreground text-[12.5px] underline underline-offset-2 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>

          <DataTable
            columns={invoiceColumns}
            data={filteredRows}
            isLoading={isFetching && rows.length === 0}
            emptyMessage="No invoices match."
            className="rounded-b-none"
            onRowClick={(invoice) =>
              void navigate({ to: '/invoices/$id', params: { id: invoice.id } })
            }
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            sorting={sorting}
            onSortingChange={onSortingChange}
            enableColumnPinning
            pinnedColumnId={pinnedColumnId}
            onPinnedColumnChange={onPinnedColumnChange}
            enableSelection={true}
            getRowId={(row) => row.id}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
          />

          <div className="bg-muted/30 text-muted-foreground mt-0 flex items-center justify-between rounded-b-lg border-x border-b px-4 py-3 text-[12.5px]">
            <span>
              Page {pageIndex + 1} of {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={pageIndex === 0}
                onClick={goToPrevPage}
                className="h-7 text-xs"
              >
                <ChevronLeft className="mr-1 size-3.5" />
                Prev
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasNextPage || isFetching}
                onClick={goToNextPage}
                className="h-7 text-xs"
              >
                Next
                <ChevronRight className="ml-1 size-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}

      <InvoicePreviewSheet
        invoiceId={invoicePreview.value}
        onOpenChange={invoicePreview.onOpenChange}
      />

      <DuplicateInvoiceDialog
        invoice={duplicateDialog.value}
        onOpenChange={duplicateDialog.onOpenChange}
      />

      <ReissueInvoiceDialog
        invoiceId={reissueDialog.value}
        onOpenChange={reissueDialog.onOpenChange}
      />

      <ConfirmDialog
        open={deleteDialog.isOpen}
        onOpenChange={deleteDialog.onOpenChange}
        title="Delete invoice?"
        description={
          deleteDialog.value?.status === 'SENT' || deleteDialog.value?.status === 'PAID'
            ? `This invoice was already ${deleteDialog.value.status.toLowerCase()}. Deleting removes it from history — consider voiding instead.`
            : `This will permanently delete invoice ${deleteDialog.value?.invoiceNumber}. If it was already sent, consider voiding instead so the audit trail is preserved.`
        }
        confirmLabel="Delete invoice"
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={markSentDialog.isOpen}
        onOpenChange={markSentDialog.onOpenChange}
        title="Mark invoice as sent?"
        description={`This marks invoice ${markSentDialog.value?.invoiceNumber} SENT without emailing the client. The invoice becomes immutable and any attached reimbursement expenses are marked as billed.`}
        confirmLabel="Mark as sent"
        isPending={transitionMutation.isPending}
        onConfirm={handleConfirmMarkSent}
      />

      <ConfirmDialog
        open={markPaidDialog.isOpen}
        onOpenChange={markPaidDialog.onOpenChange}
        title="Mark invoice as paid?"
        description={`Records a payment of ${formatCurrency(markPaidOutstandingAmount, markPaidDialog.value?.currency)} for invoice ${markPaidDialog.value?.invoiceNumber} dated today, with no note or receipt attached.`}
        confirmLabel="Mark as paid"
        isPending={
          recordPaymentMutation.isPending || markPaidPaymentsResult === undefined
        }
        onConfirm={handleConfirmMarkPaid}
      />

      <ConfirmDialog
        open={cancelRecurringDialog.isOpen}
        onOpenChange={cancelRecurringDialog.onOpenChange}
        title="Cancel recurring invoice?"
        description={`This will permanently cancel "${cancelRecurringDialog.value?.rinvLabel}". No further invoices will be generated. This cannot be undone.`}
        confirmLabel="Cancel recurring invoice"
        cancelLabel="Keep recurring invoice"
        isPending={statusMutation.isPending}
        onConfirm={handleConfirmCancelRecurringInvoice}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? '' : 's'}?`}
        description={`This will permanently delete ${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? '' : 's'}. This action cannot be undone.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={() => void handleBulkDelete()}
      />

      {isTagsEnabled && (
        <BulkTagPicker
          open={bulkTagPickerOpen}
          onOpenChange={setBulkTagPickerOpen}
          allTags={allTags.data ?? []}
          allSelectedTagIds={allSelectedTagIds}
          someSelectedTagIds={someSelectedTagIds}
          onApply={(toAdd, toRemove) => void handleBulkTagApply(toAdd, toRemove)}
          isApplying={
            assignInvoiceTagsMutation.isPending || removeInvoiceTagMutation.isPending
          }
        />
      )}
    </div>
  )
}
